/**
 * Life4Earth V2 — API Health Monitor
 *
 * Lightweight service that periodically pings key backend endpoints
 * and exposes up/down status + response times.
 *
 * Usage:
 *   startHealthMonitor();           // call once on app mount
 *   stopHealthMonitor();            // call on unmount
 *   useApiHealth();                 // React hook — returns full health snapshot
 *   getApiHealth();                 // non-React — returns current snapshot
 *
 * System admin UI only — English strings are fine per CLAUDE.md conventions.
 */

import { useSyncExternalStore } from "react";
import { API_BASE_URL, getCurrentUserGuid } from "./client";
import { getAccessToken, getIsSystemAdmin } from "../auth/AuthContext";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type EndpointGroup =
  | "Core Platform"
  | "Identity & Access"
  | "Content & Data"
  | "Communication"
  | "Admin Services";

export interface MonitoredEndpoint {
  name: string;
  path: string;
  group: EndpointGroup;
  /**
   * When true, only check this endpoint if the caller has an access token.
   * Avoids logging 401 noise to the browser console for endpoints that
   * everyone knows reject unauthenticated callers (e.g. /api/auth/me).
   * Default: false (everyone can hit it; FakeAuthMiddleware accepts
   * X-User-Guid for dev mode anonymous reads).
   */
  requiresAuth?: boolean;
  /**
   * When true, only check this endpoint if the caller is a system admin.
   * Avoids logging 401 noise for admin-only endpoints when the caller
   * is a regular user (or not signed in). The monitor will report
   * "unknown" for these endpoints in non-admin sessions, which is
   * accurate — a non-admin can't tell if those endpoints are healthy.
   */
  requiresAdmin?: boolean;
}

export type EndpointHealth = {
  name: string;
  path: string;
  group: EndpointGroup;
  status: "up" | "down" | "degraded" | "checking" | "unknown";
  statusCode: number | null;
  responseMs: number | null;
  lastChecked: number | null;       // epoch ms
  error?: string;
};

export type HealthSnapshot = {
  endpoints: EndpointHealth[];
  overallStatus: "healthy" | "degraded" | "down" | "checking";
  lastPollAt: number | null;
};

/* ------------------------------------------------------------------ */
/*  Monitored endpoints — grouped by subsystem                         */
/*                                                                     */
/*  Selection criteria:                                                */
/*    - One endpoint per critical subsystem (not every controller)     */
/*    - Must be ping-able with GET (no required entity GUIDs)          */
/*    - Lightweight endpoints preferred (counts, config, small lists)  */
/*    - Grouped so the monitor UI can display by category              */
/* ------------------------------------------------------------------ */

const ENDPOINTS: MonitoredEndpoint[] = [
  // ── Core Platform ────────────────────────────────────────
  { name: "Projects",         path: "/api/Projects",                          group: "Core Platform" },
  { name: "Communities",      path: "/api/CommunityMetadata/entities/all",    group: "Core Platform" },
  { name: "Taxonomy",         path: "/api/ProjectTaxonomy",                   group: "Core Platform" },
  { name: "System Settings",  path: "/api/SystemSettings?prefix=platform",    group: "Core Platform" },

  // ── Identity & Access ────────────────────────────────────
  { name: "Security",         path: "/api/Security/users/all",                group: "Identity & Access" },
  { name: "Authentication",   path: "/api/auth/me",                           group: "Identity & Access", requiresAuth: true },
  { name: "Workflows",        path: "/api/WorkflowRequests",                  group: "Identity & Access" },

  // ── Content & Data ───────────────────────────────────────
  { name: "KPI Config",       path: "/api/KpiConfigs",                        group: "Content & Data" },
  { name: "KPI Progress",     path: "/api/KpiProgressEntries/all",            group: "Content & Data" },
  { name: "Mission Metrics",  path: "/api/MissionMetrics/points/all",         group: "Content & Data" },
  { name: "Schedule Data",    path: "/api/ScheduleSeriesPoints/all",          group: "Content & Data" },
  { name: "Finance Data",     path: "/api/FinanceSeriesPoints/all",           group: "Content & Data" },
  { name: "Media Links",      path: "/api/MediaLinks/project/00000000-0000-0000-0000-000000000000", group: "Content & Data" },
  { name: "Embed Allowlist",  path: "/api/embed-allowlist",                   group: "Content & Data" },

  // ── Communication ────────────────────────────────────────
  { name: "Chat",             path: "/api/Contacts",                          group: "Communication" },
  { name: "Conversations",    path: "/api/Conversations",                     group: "Communication" },
  { name: "Notifications",    path: "/api/Notifications/count",               group: "Communication" },
  { name: "News",             path: "/api/news/sources",                      group: "Communication" },

  // ── Admin Services ───────────────────────────────────────
  { name: "Audit Log",        path: "/api/admin/audit-log?page=1&pageSize=1", group: "Admin Services", requiresAdmin: true },
  { name: "Waitlist",         path: "/api/admin/waitlist/counts",             group: "Admin Services", requiresAdmin: true },
  { name: "Onboarding",       path: "/api/admin/onboarding/archetypes",       group: "Admin Services", requiresAdmin: true },
  { name: "Custom Themes",    path: "/api/CustomTheme",                       group: "Admin Services" },
];

/* ------------------------------------------------------------------ */
/*  In-memory store (useSyncExternalStore pattern)                     */
/* ------------------------------------------------------------------ */

let snapshot: HealthSnapshot = {
  endpoints: ENDPOINTS.map((ep) => ({
    name: ep.name,
    path: ep.path,
    group: ep.group,
    status: "unknown",
    statusCode: null,
    responseMs: null,
    lastChecked: null,
  })),
  overallStatus: "checking",
  lastPollAt: null,
};

const listeners = new Set<() => void>();

function emitChange(): void {
  // Create new reference so useSyncExternalStore detects the change
  snapshot = { ...snapshot };
  Array.from(listeners).forEach((fn) => fn());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function getSnapshot(): HealthSnapshot {
  return snapshot;
}

/* ------------------------------------------------------------------ */
/*  Health check logic                                                 */
/* ------------------------------------------------------------------ */

const DEGRADED_THRESHOLD_MS = 3000;

/**
 * 401/403 mean the endpoint is reachable and the auth middleware is
 * functioning — the request was correctly rejected, not broken.
 * Treat these as "up" for health-monitoring purposes.
 */
const REACHABLE_STATUS_CODES = new Set([401, 403]);

async function checkEndpoint(ep: MonitoredEndpoint): Promise<EndpointHealth> {
  const start = performance.now();
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Auth headers — same pattern as apiFetch
    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    } else {
      headers["X-User-Guid"] = getCurrentUserGuid();
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    const response = await fetch(`${API_BASE_URL}${ep.path}`, {
      method: "GET",
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeout);
    const elapsed = Math.round(performance.now() - start);

    const isReachable = response.ok || REACHABLE_STATUS_CODES.has(response.status);

    const status: EndpointHealth["status"] =
      isReachable
        ? elapsed > DEGRADED_THRESHOLD_MS
          ? "degraded"
          : "up"
        : "down";

    return {
      name: ep.name,
      path: ep.path,
      group: ep.group,
      status,
      statusCode: response.status,
      responseMs: elapsed,
      lastChecked: Date.now(),
    };
  } catch (err: any) {
    const elapsed = Math.round(performance.now() - start);
    return {
      name: ep.name,
      path: ep.path,
      group: ep.group,
      status: "down",
      statusCode: null,
      responseMs: elapsed,
      lastChecked: Date.now(),
      error: err?.name === "AbortError" ? "Timeout (8s)" : (err?.message || "Network error"),
    };
  }
}

function computeOverall(endpoints: EndpointHealth[]): HealthSnapshot["overallStatus"] {
  // 'unknown' is a deliberate non-result for skipped (auth-gated) endpoints —
  // do not let it block the overall to "checking" forever. Treat skipped
  // endpoints as not-counting and decide overall from the rest.
  const considered = endpoints.filter((e) => e.status !== "unknown");
  if (considered.length === 0) return "checking";
  if (considered.some((e) => e.status === "checking")) return "checking";
  const downCount = considered.filter((e) => e.status === "down").length;
  if (downCount === considered.length) return "down";
  if (downCount > 0 || considered.some((e) => e.status === "degraded")) return "degraded";
  return "healthy";
}

/**
 * Decide whether an endpoint should be checked given the current auth
 * state. Endpoints that require auth or admin are skipped (status='unknown')
 * when the caller isn't qualified, which avoids logging 401 noise to the
 * browser console for endpoints we already know will reject us.
 */
function shouldCheck(ep: MonitoredEndpoint): boolean {
  if (ep.requiresAdmin && !getIsSystemAdmin()) return false;
  if (ep.requiresAuth && !getAccessToken()) return false;
  return true;
}

async function runHealthCheck(): Promise<void> {
  // Mark all as checking
  snapshot = {
    ...snapshot,
    endpoints: snapshot.endpoints.map((ep) => ({ ...ep, status: "checking" as const })),
    overallStatus: "checking",
  };
  emitChange();

  // Check eligible endpoints in parallel; skipped endpoints stay 'unknown'
  // (an honest signal — a non-admin caller genuinely can't tell whether
  // admin endpoints are healthy; pretending otherwise via spurious 401
  // probes pollutes the console without adding information).
  const results = await Promise.all(
    ENDPOINTS.map((ep) => {
      if (!shouldCheck(ep)) {
        return Promise.resolve<EndpointHealth>({
          name: ep.name,
          path: ep.path,
          group: ep.group,
          status: "unknown",
          statusCode: null,
          responseMs: null,
          lastChecked: Date.now(),
          error: ep.requiresAdmin
            ? "Skipped — requires system admin"
            : "Skipped — requires sign-in",
        });
      }
      return checkEndpoint(ep);
    })
  );

  snapshot = {
    endpoints: results,
    overallStatus: computeOverall(results),
    lastPollAt: Date.now(),
  };
  emitChange();
}

/* ------------------------------------------------------------------ */
/*  Polling lifecycle                                                  */
/* ------------------------------------------------------------------ */

const POLL_INTERVAL_MS = 30_000; // 30 seconds
let pollTimer: ReturnType<typeof setInterval> | null = null;

export function startHealthMonitor(): void {
  if (pollTimer) return; // already running
  runHealthCheck(); // immediate first check
  pollTimer = setInterval(runHealthCheck, POLL_INTERVAL_MS);
}

export function stopHealthMonitor(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/** Force an immediate re-check (e.g. from the Refresh button). */
export function refreshHealth(): void {
  runHealthCheck();
}

/* ------------------------------------------------------------------ */
/*  React hook                                                         */
/* ------------------------------------------------------------------ */

export function useApiHealth(): HealthSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Non-React accessor for the current snapshot. */
export function getApiHealth(): HealthSnapshot {
  return snapshot;
}

/**
 * Convenience: is a specific service up?
 * Returns true if the named endpoint is "up", false otherwise.
 */
export function isServiceUp(name: string): boolean {
  const ep = snapshot.endpoints.find((e) => e.name === name);
  return ep?.status === "up" || ep?.status === "degraded";
}
